import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Search from '@mui/icons-material/Search';
import Upload from '@mui/icons-material/Upload';
import { useTranslation } from 'react-i18next';
import StatusPill from '../components/StatusPill';
import priceListApi, { PriceListItem, ImportResult } from '../services/priceListService';
import { formatApiError } from '../utils/formatApiError';
import { fmtMoney } from '../utils/formatMoney';

const PAGE_LIMIT = 30;

const parseOptionalFloat = (s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
};

const parseOptionalInt = (s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
};

type FormState = {
  part_number: string;
  description: string;
  item_name: string;
  category: string;
  upc_code: string;
  master_carton_qty: string;
  us_list_price: string;
  tier1_price: string;
  tier2_price: string;
  tier3_price: string;
  tier4_price: string;
  tier5_price: string;
  dfp_cost: string;
  is_active: boolean;
};

const emptyForm = (): FormState => ({
  part_number: '',
  description: '',
  item_name: '',
  category: '',
  upc_code: '',
  master_carton_qty: '',
  us_list_price: '',
  tier1_price: '',
  tier2_price: '',
  tier3_price: '',
  tier4_price: '',
  tier5_price: '',
  dfp_cost: '',
  is_active: true,
});

const itemToForm = (item: PriceListItem): FormState => ({
  part_number: item.part_number ?? '',
  description: item.description ?? '',
  item_name: item.item_name ?? '',
  category: item.category ?? '',
  upc_code: item.upc_code ?? '',
  master_carton_qty: item.master_carton_qty != null ? String(item.master_carton_qty) : '',
  us_list_price: item.us_list_price != null ? String(item.us_list_price) : '',
  tier1_price: item.tier1_price != null ? String(item.tier1_price) : '',
  tier2_price: item.tier2_price != null ? String(item.tier2_price) : '',
  tier3_price: item.tier3_price != null ? String(item.tier3_price) : '',
  tier4_price: item.tier4_price != null ? String(item.tier4_price) : '',
  tier5_price: item.tier5_price != null ? String(item.tier5_price) : '',
  dfp_cost: item.dfp_cost != null ? String(item.dfp_cost) : '',
  is_active: item.is_active,
});

const PriceListPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const [items, setItems] = useState<PriceListItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});

  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PriceListItem | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importResultOpen, setImportResultOpen] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(id);
  }, [search]);

  useEffect(() => {
    priceListApi.categories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const fetchPage = useCallback(
    async (skip: number, append: boolean) => {
      const data = await priceListApi.list({
        search: debouncedSearch.trim() || undefined,
        category: categoryFilter.trim() || undefined,
        skip,
        limit: PAGE_LIMIT,
      });
      if (append) {
        setItems((prev) => [...prev, ...data]);
      } else {
        setItems(data);
      }
      setHasMore(data.length === PAGE_LIMIT);
    },
    [debouncedSearch, categoryFilter],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHasMore(true);
    fetchPage(0, false)
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, categoryFilter, fetchPage]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      await fetchPage(items.length, true);
    } catch {
      setSnackbar({ open: true, message: 'Could not load more items', severity: 'error' });
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMore, items.length, fetchPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || loading || !hasMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: '120px', threshold: 0 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [loadMore, loading, hasMore, items.length]);

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openNew = () => {
    setEditingItem(null);
    setForm(emptyForm());
    setEditOpen(true);
  };

  const openEdit = (item: PriceListItem) => {
    setEditingItem(item);
    setForm(itemToForm(item));
    setEditOpen(true);
  };

  const buildPayload = (): Partial<PriceListItem> => ({
    part_number: form.part_number.trim(),
    description: form.description.trim() || null,
    item_name: form.item_name.trim() || null,
    category: form.category.trim() || null,
    upc_code: form.upc_code.trim() || null,
    master_carton_qty: parseOptionalInt(form.master_carton_qty),
    us_list_price: parseOptionalFloat(form.us_list_price),
    tier1_price: parseOptionalFloat(form.tier1_price),
    tier2_price: parseOptionalFloat(form.tier2_price),
    tier3_price: parseOptionalFloat(form.tier3_price),
    tier4_price: parseOptionalFloat(form.tier4_price),
    tier5_price: parseOptionalFloat(form.tier5_price),
    dfp_cost: parseOptionalFloat(form.dfp_cost),
    is_active: form.is_active,
  });

  const handleSave = async () => {
    if (!form.part_number.trim()) {
      setSnackbar({ open: true, message: 'Part number is required', severity: 'error' });
      return;
    }
    try {
      const payload = buildPayload();
      if (editingItem) {
        await priceListApi.update(editingItem.id, payload);
      } else {
        await priceListApi.create(payload);
      }
      setEditOpen(false);
      setSnackbar({ open: true, message: 'Saved successfully', severity: 'success' });
      try {
        await fetchPage(0, false);
      } catch (refetchErr: unknown) {
        setSnackbar({ open: true, message: formatApiError(refetchErr, 'List refresh failed'), severity: 'error' });
      }
    } catch (e: unknown) {
      setSnackbar({ open: true, message: formatApiError(e, t('common.save')), severity: 'error' });
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const result = await priceListApi.import(file);
      setImportResult(result);
      setImportResultOpen(true);
      setSnackbar({
        open: true,
        severity: result.errors.length ? 'error' : 'success',
        message:
          result.errors.length > 0
            ? `Import finished with ${result.errors.length} error(s)`
            : `Created ${result.created}, updated ${result.updated}`,
      });
      try {
        await fetchPage(0, false);
      } catch (refetchErr: unknown) {
        setSnackbar({ open: true, message: formatApiError(refetchErr, 'List refresh failed'), severity: 'error' });
      }
      priceListApi.categories().then(setCategories).catch(() => {});
    } catch (err: unknown) {
      setSnackbar({ open: true, message: formatApiError(err, 'Import failed'), severity: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const mobileTouchIconSx = { minWidth: 48, minHeight: 48 };
  const mobileButtonSx = isMobile ? { minHeight: 48, py: 1.25 } : {};

  const displayLine = (item: PriceListItem) =>
    [item.description, item.item_name].filter(Boolean).join(' — ') || '—';

  return (
    <Box sx={{ pb: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        Price list
      </Typography>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <TextField
          fullWidth
          size={isMobile ? 'medium' : 'small'}
          placeholder="Search part number, description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <TextField
            select
            label="Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
            size={isMobile ? 'medium' : 'small'}
          >
            <MenuItem value="">All categories</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, flex: 1, justifyContent: { sm: 'flex-end' } }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              variant="outlined"
              startIcon={importing ? <CircularProgress size={20} color="inherit" /> : <Upload />}
              onClick={handleImportClick}
              disabled={importing}
              sx={mobileButtonSx}
              fullWidth={isMobile}
            >
              Import
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={openNew} sx={mobileButtonSx} fullWidth={isMobile}>
              New item
            </Button>
          </Box>
        </Box>

        {loading && !items.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((item) => (
              <Card key={item.id} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleExpanded(item.id)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault();
                          toggleExpanded(item.id);
                        }
                      }}
                      sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} component="div">
                        {item.part_number}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {displayLine(item)}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        <Chip size="small" label={`MSRP ${fmtMoney(item.us_list_price)}`} />
                        <Chip size="small" label={`DFP ${fmtMoney(item.dfp_cost)}`} variant="outlined" />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <IconButton
                        aria-label={expandedIds[item.id] ? 'Collapse' : 'Expand tiers'}
                        onClick={() => toggleExpanded(item.id)}
                        sx={mobileTouchIconSx}
                      >
                        {expandedIds[item.id] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                      <IconButton aria-label="Edit" onClick={() => openEdit(item)} sx={mobileTouchIconSx}>
                        <Edit />
                      </IconButton>
                    </Box>
                  </Box>
                  <Collapse in={!!expandedIds[item.id]}>
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Tier prices
                      </Typography>
                      <Typography variant="body2">Tier 1: {fmtMoney(item.tier1_price)}</Typography>
                      <Typography variant="body2">Tier 2: {fmtMoney(item.tier2_price)}</Typography>
                      <Typography variant="body2">Tier 3: {fmtMoney(item.tier3_price)}</Typography>
                      <Typography variant="body2">Tier 4: {fmtMoney(item.tier4_price)}</Typography>
                      <Typography variant="body2">Tier 5: {fmtMoney(item.tier5_price)}</Typography>
                      {item.category && (
                        <Chip size="small" label={item.category} sx={{ mt: 1 }} />
                      )}
                      <Box sx={{ mt: 1 }}>
                        <StatusPill value={item.is_active} trueLabel="Active" falseLabel="Inactive" />
                      </Box>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            ))}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Part number</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">MSRP</TableCell>
                  <TableCell align="right">Tier 1</TableCell>
                  <TableCell align="right">Tier 2</TableCell>
                  <TableCell align="right">Tier 3</TableCell>
                  <TableCell align="right">Tier 4</TableCell>
                  <TableCell align="right">Tier 5</TableCell>
                  <TableCell align="right">DFP cost</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.part_number}</TableCell>
                    <TableCell>{displayLine(row)}</TableCell>
                    <TableCell>{row.category || '—'}</TableCell>
                    <TableCell align="right">{fmtMoney(row.us_list_price)}</TableCell>
                    <TableCell align="right">{fmtMoney(row.tier1_price)}</TableCell>
                    <TableCell align="right">{fmtMoney(row.tier2_price)}</TableCell>
                    <TableCell align="right">{fmtMoney(row.tier3_price)}</TableCell>
                    <TableCell align="right">{fmtMoney(row.tier4_price)}</TableCell>
                    <TableCell align="right">{fmtMoney(row.tier5_price)}</TableCell>
                    <TableCell align="right">{fmtMoney(row.dfp_cost)}</TableCell>
                    <TableCell>
                      <StatusPill value={row.is_active} trueLabel="Active" falseLabel="Inactive" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openEdit(row)} aria-label="Edit">
                        <Edit fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box ref={sentinelRef} sx={{ height: 8 }} />
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}
          </TableContainer>
        )}

        {!loading && items.length === 0 && (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No items match your filters.
          </Typography>
        )}
      </Paper>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingItem ? 'Edit item' : 'New item'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            required
            margin="dense"
            label="Part number"
            value={form.part_number}
            onChange={(e) => setForm({ ...form, part_number: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            minRows={2}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Item name"
            value={form.item_name}
            onChange={(e) => setForm({ ...form, item_name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Category"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label="UPC code"
            value={form.upc_code}
            onChange={(e) => setForm({ ...form, upc_code: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Master carton qty"
            value={form.master_carton_qty}
            onChange={(e) => setForm({ ...form, master_carton_qty: e.target.value })}
            inputMode="numeric"
          />
          <TextField
            fullWidth
            margin="dense"
            label="US list price (MSRP)"
            value={form.us_list_price}
            onChange={(e) => setForm({ ...form, us_list_price: e.target.value })}
            inputMode="decimal"
          />
          <TextField
            fullWidth
            margin="dense"
            label="Tier 1 price"
            value={form.tier1_price}
            onChange={(e) => setForm({ ...form, tier1_price: e.target.value })}
            inputMode="decimal"
          />
          <TextField
            fullWidth
            margin="dense"
            label="Tier 2 price"
            value={form.tier2_price}
            onChange={(e) => setForm({ ...form, tier2_price: e.target.value })}
            inputMode="decimal"
          />
          <TextField
            fullWidth
            margin="dense"
            label="Tier 3 price"
            value={form.tier3_price}
            onChange={(e) => setForm({ ...form, tier3_price: e.target.value })}
            inputMode="decimal"
          />
          <TextField
            fullWidth
            margin="dense"
            label="Tier 4 price"
            value={form.tier4_price}
            onChange={(e) => setForm({ ...form, tier4_price: e.target.value })}
            inputMode="decimal"
          />
          <TextField
            fullWidth
            margin="dense"
            label="Tier 5 price"
            value={form.tier5_price}
            onChange={(e) => setForm({ ...form, tier5_price: e.target.value })}
            inputMode="decimal"
          />
          <TextField
            fullWidth
            margin="dense"
            label="DFP cost"
            value={form.dfp_cost}
            onChange={(e) => setForm({ ...form, dfp_cost: e.target.value })}
            inputMode="decimal"
          />
          <FormControlLabel
            control={
              <Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            }
            label="Active"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={isMobile ? { minHeight: 48 } : {}}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} sx={isMobile ? { minHeight: 48 } : {}}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importResultOpen} onClose={() => setImportResultOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import results</DialogTitle>
        <DialogContent>
          {importResult && (
            <>
              <Typography variant="body2" gutterBottom>
                Created: <strong>{importResult.created}</strong>
              </Typography>
              <Typography variant="body2" gutterBottom>
                Updated: <strong>{importResult.updated}</strong>
              </Typography>
              {importResult.errors.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Errors ({importResult.errors.length})
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 240, overflow: 'auto' }}>
                    {importResult.errors.map((err, i) => (
                      <Typography key={i} component="li" variant="body2">
                        {err}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResultOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PriceListPage;
